// File: src/home/Hblog.jsx
import React, { useEffect, useMemo, useState } from "react";
import "../blog/blog.css";
import Heading from "../common/heading/Heading";
import { collection, getDocs, orderBy, query, limit } from "firebase/firestore";
import { db } from "../../firebase";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

// ───────── Utilities ─────────
function formatDate(ts) {
  try {
    const d = ts?.toDate ? ts.toDate() : ts instanceof Date ? ts : null;
    return d
      ? d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
      : "";
  } catch {
    return "";
  }
}

function firstParagraphText(body) {
  if (!Array.isArray(body)) return "";
  const p = body.find((b) => b?.type === "paragraph" && typeof b?.html === "string");
  if (!p) return "";
  const text = p.html.replace(/<[^>]+>/g, " ");
  return text.replace(/\s+/g, " ").trim();
}

const Hblog = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const ref = collection(db, "blogs");
        let list = [];
        try {
          const snap = await getDocs(query(ref, orderBy("publishedAt", "desc"), limit(12)));
          list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        } catch {
          const snap = await getDocs(query(ref, orderBy("createdAt", "desc"), limit(12)));
          list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        }
        setBlogs(list);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const cards = useMemo(() => {
    return (blogs || []).map((b) => {
      const fallback = firstParagraphText(b.body);
      const excerpt = (b.excerpt || fallback || "").trim();
      const shortDesc = excerpt.length > 180 ? `${excerpt.slice(0, 177)}…` : excerpt;

      return {
        _id: b.id,
        slug: b.slug || b.id,
        cover: b.mainPhotoURL || b.coverURL || "https://picsum.photos/seed/hblog/1200/630",
        type: b.category || "Blog",
        date: formatDate(b.publishedAt || b.createdAt),
        com: typeof b.commentsCount === "number" ? b.commentsCount : 0,
        title: b.title || "Untitled",
        desc: shortDesc,
      };
    });
  }, [blogs]);

  const openBlog = (slugOrId) => navigate(`/blogs/${slugOrId}`);

  // Duplicate cards for seamless loop
  const carouselItems = useMemo(() => {
    if (cards.length === 0) return [];
    return [...cards, ...cards]; // Duplicate to cover the width
  }, [cards]);

  return (
    <section className="blog">
      <div className="container">
        <Heading subtitle="OUR BLOG" title="Recent From Blog" />

        <div className="blog-carousel-container" style={{ overflow: "hidden", padding: "20px 0", cursor: "grab" }}>
          {loading ? (
            <div className="flexSB" style={{ gap: "20px" }}>
              {Array.from({ length: 3 }).map((_, i) => (
                <div className="items shadow" key={`skel-${i}`} style={{ width: "360px", flexShrink: 0 }}>
                  <div className="img" style={{ background: "#111", height: 180 }} />
                  <div className="text" style={{ padding: 16 }}>
                    <div style={{ height: 20, background: "#222", width: "70%", marginBottom: 10 }} />
                    <div style={{ height: 15, background: "#222", width: "90%" }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <motion.div
              className="blog-track"
              style={{ display: "flex", gap: "20px", width: "fit-content" }}
              animate={{
                x: [0, -((360 + 20) * cards.length)] // 360 width + 20 gap
              }}
              transition={{
                x: {
                  repeat: Infinity,
                  repeatType: "loop",
                  duration: cards.length * 8, // Adjust speed based on item count
                  ease: "linear",
                },
              }}
            >
              {carouselItems.map((val, idx) => (
                <div className="items shadow" key={`${val._id}-${idx}`}>
                  <div
                    className="img"
                    onClick={() => openBlog(val.slug)}
                    style={{ cursor: "pointer" }}
                  >
                    <img src={val.cover} alt={val.title} loading="lazy" />
                  </div>
                  <div className="text">
                    <div className="admin flexSB">
                      <span>
                        <i className="fa fa-user"></i>
                        <label htmlFor="">{val.type}</label>
                      </span>
                      <span>
                        <i className="fa fa-calendar-alt"></i>
                        <label htmlFor="">{val.date}</label>
                      </span>
                      <span>
                        <i className="fa fa-comments"></i>
                        <label htmlFor="">{val.com}</label>
                      </span>
                    </div>
                    <h1
                      onClick={() => openBlog(val.slug)}
                      style={{ cursor: "pointer" }}
                    >
                      {val.title}
                    </h1>
                    <p>{val.desc}</p>
                    <button
                      className="wb-readmore"
                      type="button"
                      onClick={() => openBlog(val.slug)}
                    >
                      Read More
                    </button>
                  </div>
                </div>
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Hblog;
